import { useDispatch, useSelector } from "react-redux";
import MedicalHistory from "../components/patientTabs/MedicalHistory";
import { useTranslation } from "react-i18next";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getPatientById } from "../redux/patientSlice";
import CommonLoader from "../components/CommonLoader";
import ErrorPage from "../components/ErrorPage";
import { selectDateTime as errorimg } from '../assets/img/illustrations';

function MyAppointment() {
    const { t } = useTranslation("myappointment");
    const isInitialCall = useRef(true);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { selectedPatient, isLoading, error } = useSelector((state) => state.patient);
    const patientId = localStorage.getItem("ID");

    useEffect(() => {
        if (isInitialCall.current && patientId) {
            dispatch(getPatientById(patientId));
            isInitialCall.current = false;
        }
    }, [dispatch, patientId]);

    if (isLoading) {
        return (
            <div data-testid="my-appointment-loading-container" className="bg-white rounded-2xl p-6 w-full h-full flex items-center justify-center shadow-lg">
                <div className="flex flex-col items-center justify-center gap-4 py-6">
                    <CommonLoader />
                    <span data-testid="my-appointment-loading-text" className="text-xl text-[#4ABDBD] font-semibold">{t('patientappointment.loading')}</span>
                </div>
            </div>
        );
    }

    if (error) {
        if (error?.status >= 500 && error?.status <= 599) {
            return <ErrorPage error={t('errors.serverError')} pageName={t('myappointment.title')} />;
        }
        if (error?.message === 'Network Error') {
            return <ErrorPage error={t('errors.networkError')} pageName={t('myappointment.title')} />;
        }
        return <ErrorPage error={t('errors.genericError')} pageName={t('myappointment.title')} />;
    }

    if (!selectedPatient) {
        return (
            <ErrorPage error={t('errors.noPatientData')} pageName={t('myappointment.title')} />
        );
    }

    // Check if patient profile is incomplete - restrict viewing appointments
    if (selectedPatient && selectedPatient.profileStatus === 'incomplete' && !isLoading) {
        return (
            <div data-testid="my-appointment-profile-incomplete-container" className="h-full bg-white rounded-2xl p-4 sm:p-6 md:p-8 lg:p-10 flex items-center justify-center">
                <div className="text-center max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl">
                    <img
                        data-testid="my-appointment-profile-incomplete-image"
                        src={errorimg}
                        alt={t('myappointment.profile_incomplete_alt', 'Profile Incomplete')}
                        className="mx-auto w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 lg:w-56 lg:h-56 xl:w-64 xl:h-64 object-contain"
                    />
                    <h2 data-testid="my-appointment-profile-incomplete-title" className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#68c0c0] mb-3 sm:mb-4">
                        {t('myappointment.unable_to_view', 'Unable to View Appointments')}
                    </h2>
                    <p data-testid="my-appointment-profile-incomplete-message" className="text-sm sm:text-base md:text-lg text-gray-800 mb-2 sm:mb-3 md:mb-4">
                        {t('myappointment.profile_incomplete_message', 'Your profile is incomplete. Please complete your profile to view appointments.')}
                    </p>
                    <p data-testid="my-appointment-profile-incomplete-update-message" className="text-xs sm:text-sm md:text-base text-gray-600 mb-4 sm:mb-6 md:mb-8">
                        {t('myappointment.update_profile_message', 'Please update your profile information to continue.')}
                    </p>
                    <button
                        data-testid="my-appointment-go-to-profile-button"
                        onClick={() => navigate('/patientProfile')}
                        className="px-3 py-1.5 sm:px-4 sm:py-2 md:px-5 md:py-2.5 cursor-pointer bg-[#4ABDBD] text-white font-semibold rounded-lg shadow-sm text-xs sm:text-sm md:text-base transition-all hover:bg-[#3aa8a8] focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] focus:ring-opacity-50"
                    >
                        {t('myappointment.go_to_profile', 'Go to Profile')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="my-appointment-page" className="bg-white rounded-2xl p-6 shadow-lg mx-auto w-full h-auto">
            <h2 data-testid="my-appointment-title" className="text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent mb-6">
                {t("myappointment.title")}
            </h2>
                <MedicalHistory medicalHistory={selectedPatient.medicalHistory} />
        </div>
    );
}

export default MyAppointment;